import React, { useState, useEffect, useRef, useCallback } from "react";
import { addSyllabus, deleteSyllabus, approveSyllabus } from "../../api/api";
import { useGlobalData } from "../../context/GlobalDataContext";
import "../superadmin/AdminPanel.css";

function HrSyllabusAdminPanel({ darkMode }) {
  const { threeTable, fetchThreeTableSection } = useGlobalData();
  const [jobs, setJobs] = useState([]);
  const [syllabuses, setSyllabuses] = useState([]);
  const [syllabusesOffset, setSyllabusesOffset] = useState(0);
  const [syllabusesHasMore, setSyllabusesHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const listRef = useRef(null);
  const PAGE_LIMIT = 29;
  const [formData, setFormData] = useState({ jobSlug: "" });
  const role = localStorage.getItem("role");

  // Initial load (global state)
  useEffect(() => {
    let ignore = false;
    async function fetchInitial() {
      await fetchThreeTableSection("syllabuses", false, { limit: PAGE_LIMIT, offset: 0 });
      if (!ignore) setJobs(threeTable.jobs || []);
    }
    fetchInitial();
    return () => { ignore = true; };
    // eslint-disable-next-line
  }, []);

  // Sync syllabuses from global state
  useEffect(() => {
    setSyllabuses((threeTable.syllabuses || []).slice());
    setSyllabusesOffset(threeTable.syllabuses?.length || 0);
    setSyllabusesHasMore((threeTable.syllabuses?.length || 0) === PAGE_LIMIT);
    setJobs(threeTable.jobs || []);
  }, [threeTable.syllabuses, threeTable.jobs]);

  // Infinite scroll fetch
  const fetchMoreSyllabuses = useCallback(async () => {
    if (!syllabusesHasMore || isFetchingMore) return;
    setIsFetchingMore(true);
    await fetchThreeTableSection("syllabuses", false, { limit: PAGE_LIMIT, offset: syllabusesOffset });
    setIsFetchingMore(false);
  }, [syllabusesHasMore, isFetchingMore, syllabusesOffset, fetchThreeTableSection]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!syllabusesHasMore || isFetchingMore) return;
    const el = listRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.bottom <= window.innerHeight + 200) {
      fetchMoreSyllabuses();
    }
  }, [syllabusesHasMore, isFetchingMore, fetchMoreSyllabuses]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await addSyllabus(formData);
    setFormData({ jobSlug: "" });
    await fetchThreeTableSection("syllabuses", true, { limit: PAGE_LIMIT, offset: 0 });
  };

  const handleApprove = async (id) => {
    await approveSyllabus(id);
    await fetchThreeTableSection("syllabuses", true, { limit: PAGE_LIMIT, offset: 0 });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this syllabus?")) {
      await deleteSyllabus(id);
      await fetchThreeTableSection("syllabuses", true, { limit: PAGE_LIMIT, offset: 0 });
    }
  };

  const getJobTitle = (jobSlug) => jobs.find((job) => job.slug === jobSlug)?.title || jobSlug;
  const filteredSyllabuses = syllabuses.filter((syllabus) => !formData.jobSlug || syllabus.jobSlug === formData.jobSlug);

  if (role !== "hr") {
    return <div className="admin-panel-error">❌ Access denied! Only HR can access this panel.</div>;
  }

  return (
    <div className={`resource-admin-panel resource-admin-panel--emerald${darkMode ? " dark-mode" : ""}`}> 
      <div className="resource-admin-panel__header">
        <div>
          <h2 className="resource-admin-panel__heading">HR Syllabus Admin Panel</h2>
          <p className="resource-admin-panel__subtext">Syllabus records ke liye same polished workflow diya gaya hai, jisme save, review, live preview aur delete actions ek clean layout me milte hain.</p>
        </div>
        <div className="resource-admin-panel__pill">Syllabus</div>
      </div>

      <form className="resource-admin-panel__form" onSubmit={handleSubmit}>
        <div className="resource-admin-panel__field">
          <label>Job:</label>
          <select
            value={formData.jobSlug}
            onChange={(e) => setFormData({ ...formData, jobSlug: e.target.value })}
            required
          >
            <option value="">Select Job</option>
            {jobs.map((job) => (
              <option key={job.slug} value={job.slug}>
                {job.title}
              </option>
            ))}
          </select>
        </div>
        <button type="submit">Add Syllabus</button>
      </form>

      <div className="resource-admin-panel__list" ref={listRef} style={{ maxHeight: "800px", overflowY: "auto" }}>
        <h3>Syllabus Entries</h3>
        <ul>
          {filteredSyllabuses.map((syllabus) => {
            const isPending = syllabus.status === "pending";
            const statusColor = isPending ? "#f7b500" : "#2ecc40";
            return (
              <li key={syllabus._id} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span>{getJobTitle(syllabus.jobSlug)}</span>
                <span style={{
                  background: statusColor,
                  color: "#fff",
                  borderRadius: 6,
                  padding: "2px 10px",
                  fontWeight: 600,
                  fontSize: 13
                }}>{syllabus.status === "pending" ? "Pending" : "Approved"}</span>
                {isPending && (
                  <>
                    <button onClick={() => handleApprove(syllabus._id)} style={{ background: "#f7b500", color: "#fff", marginLeft: 8 }}>Approve</button>
                    <button onClick={() => handleDelete(syllabus._id)} style={{ marginLeft: 8 }}>Delete</button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
        {isFetchingMore && <div style={{ textAlign: "center", margin: 10 }}>Loading more...</div>}
        {!syllabusesHasMore && !isFetchingMore && syllabuses.length > 0 && <div style={{ textAlign: "center", margin: 10, color: "#888" }}>No more syllabuses.</div>}
      </div>
    </div>
  );
}

export default HrSyllabusAdminPanel;
